import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Lock, CheckCircle } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.tsx'
import { Button } from '../ui/button.tsx'
import { Input } from '../ui/input.tsx'
import { Label } from '../ui/label.tsx'
import { Alert, AlertDescription } from '../ui/alert.tsx'
import { ApiClient } from '../../lib/api/client.ts'

export function ChangePasswordSection() {
  const { t } = useTranslation()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)

    if (newPassword.length < 6) {
      setError(t('settings.changePassword.tooShort'))
      return
    }

    if (newPassword !== confirmPassword) {
      setError(t('settings.changePassword.mismatch'))
      return
    }

    if (currentPassword === newPassword) {
      setError(t('settings.changePassword.samePassword'))
      return
    }

    setIsLoading(true)

    try {
      await ApiClient.changePassword(currentPassword, newPassword)
      setSuccess(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.changePassword.failed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Lock className="h-5 w-5 text-[#26247B]" />
          <CardTitle className="text-lg">
            {t('settings.changePassword.title')}
          </CardTitle>
        </div>
        <CardDescription>
          {t('settings.changePassword.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="currentPassword">
              {t('settings.changePassword.currentPassword')}
            </Label>
            <Input
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="current-password"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword">
              {t('settings.changePassword.newPassword')}
            </Label>
            <Input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              disabled={isLoading}
              autoComplete="new-password"
            />
            <p className="text-xs text-muted-foreground">
              {t('settings.changePassword.minLength')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmNewPassword">
              {t('settings.changePassword.confirmPassword')}
            </Label>
            <Input
              id="confirmNewPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              disabled={isLoading}
              autoComplete="new-password"
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="border-[#63C36F] bg-[#E9F1EE]/50">
              <CheckCircle className="h-4 w-4 text-[#4E8A56]" />
              <AlertDescription className="text-[#4E8A56]">
                {t('settings.changePassword.success')}
              </AlertDescription>
            </Alert>
          )}

          <Button type="submit" disabled={isLoading}>
            {isLoading
              ? t('settings.changePassword.submitting')
              : t('settings.changePassword.submit')}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
